import {
  CcfBox,
  CcfChatIcon,
  CcfEmailIcon,
  CcfFacebookIcon,
  CcfInstaIcon,
  CcfLinkedinIcon,
  CcfPhoneInboundIcon,
  CcfSmsIcon,
  CcfTypography,
  CcfXIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import styles from './ccf-interactions-card.module.scss';
import { Card, CardContent, List, ListItem, Popover } from '@mui/material';
import { KeyboardArrowRight } from '@mui/icons-material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { activeContactSelector } from '../../../../ccf-inbox/ccf-inbox.slice';

/* eslint-disable-next-line */
export interface CcfInteractionsCardProps {
  interaction: any;
  onClickInteractionCard: () => void;
}

/**
 * CcfInteractionsCard - returns Interactions Card in consumer card interactions tab
 * @param props - CcfInteractionsCardProps
 * @example - `<CcfInteractionsCard />`
 */
export function CcfInteractionsCard(props: CcfInteractionsCardProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { interaction, onClickInteractionCard } = props;
  const activeContact = useSelector(activeContactSelector);
  const [translate] = useTranslator();

  /**
   * Function to open the popover
   * @example handlePopoverOpen()
   */
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Function to close the popover
   * @example handlePopoverClose()
   */
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  /**
   * Function to render Icon for Channel
   * @example renderIconForChannel()
   */
  const renderIconForChannel = (channel: string) => {
    switch (channel) {
      case 'Voice':
        return <CcfPhoneInboundIcon className={styles.channelIcon} />;
      case 'Email':
        return <CcfEmailIcon className={styles.channelIcon} />;
      case 'SMS':
        return <CcfSmsIcon className={styles.channelIcon} />;
      case 'Chat':
        return <CcfChatIcon className={styles.channelIcon} />;
      case 'X':
        return <CcfXIcon className={styles.channelIcon}/>;
      case 'Instagram':
        return <CcfInstaIcon className={styles.channelIcon}/>;
      case 'Facebook':
        return <CcfFacebookIcon className={styles.channelIcon}/>;
      case 'LinkedIn':
        return <CcfLinkedinIcon className={styles.channelIcon}/>;
      default:
        return null;
    }
  };

  /**
   * Function to convert the timeformat to 'hh:mm a' or 'hh:mm:ss a' format
   * @example convertTimeFormatHandler()
   */
  const convertTimeFormatHandler = (
    timeString: string,
    format: string
  ): string => {
    const date = new Date(timeString);

    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';

    const seconds = date.getSeconds();

    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    const secondsStr = seconds < 10 ? '0' + seconds : seconds;

    let formattedTime = '';
    if (format === 'hh:mm:ss a') {
      formattedTime = `${hours}:${minutesStr}:${secondsStr} ${ampm}`;
    } else if (format === 'hh:mm a') {
      formattedTime = `${hours}:${minutesStr} ${ampm}`;
    }

    return formattedTime;
  };

  /**
   * Function to fetch autosummary referance from the autosummary string
   * @example getFirstAutoSummaryItem()
   */
  const getFirstAutoSummaryItem = (autoSummary: string) => {
    // Split the string by commas or periods
    const items = autoSummary?.split(/[,.]/);
    // Return the first item, trimmed of any leading/trailing whitespace
    return items[0]?.trim();
  };

  return (
    <>
      <Card
        variant="outlined"
        style={{ marginBottom: '5px', cursor: 'pointer' }}
        onClick={onClickInteractionCard}
      >
        <CardContent className={styles.interactionsCardContent}>
          <CcfBox className={styles.interactionDetailsBlock}>
            <CcfBox>{renderIconForChannel(interaction.media)}</CcfBox>
            <CcfBox className={styles.interactionDetails}>
              <CcfTypography className={styles.interactionTitle}>
                {interaction.media === 'Email'
                  ? activeContact?.customer.emailId
                  : activeContact?.customer.phoneNo}
              </CcfTypography>
              <CcfTypography className={styles.skillName}>
                {interaction.contactRequest}
              </CcfTypography>
              <CcfTypography className={styles.agentName}>
                #{interaction.contactTrackingId} | {interaction.agentName}
              </CcfTypography>
            </CcfBox>
          </CcfBox>
          <CcfBox
            className={styles.interactionStatusBlock}
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
          >
            <CcfBox className={styles.interactionStatusDetail}>
              <CcfBox className={styles.interactionTime}>
                {convertTimeFormatHandler(interaction.date, 'hh:mm a')}
              </CcfBox>
              <CcfBox
                style={{
                  color:
                    interaction.status.toLowerCase() === 'open'
                      ? '#0B82DD'
                      : '#5f6367',
                }}
                className={styles.interactionStatus}
              >
                {translate(interaction.status.toLowerCase())}
              </CcfBox>
            </CcfBox>
            <CcfBox className={styles.expandBlock}>
              <KeyboardArrowRight />
            </CcfBox>
          </CcfBox>
        </CardContent>
      </Card>
      <Popover
        id="interaction-autosummary-popover"
        open={open}
        keepMounted
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        style={{
          pointerEvents: 'none',
        }}
      >
        <CcfBox className={styles.popoverContainer}>
          <CcfBox className={styles.popoverContent}>
            <CcfBox className={styles.timestampBlock}>
              <CcfBox>
                <CcfTypography
                  className={styles.title}
                  translationKey={'start'}
                />
                <CcfTypography className={styles.time}>
                  {convertTimeFormatHandler(interaction.date, 'hh:mm:ss a')}
                </CcfTypography>
              </CcfBox>
              <CcfBox>
                <CcfTypography
                  className={styles.title}
                  translationKey={'end'}
                />
                <CcfTypography className={styles.time}>
                  {interaction.endTime}
                </CcfTypography>
              </CcfBox>
            </CcfBox>
            <CcfBox className={styles.autosummaryBlock}>
              <CcfTypography
                className={styles.title}
                translationKey={'autoSummary'}
              />
              <ul className={styles.list}>
                <li className={styles.listItem}>
                  {getFirstAutoSummaryItem(interaction.autoSummary)}
                </li>
                <li className={styles.listItem}>Status was still pending</li>
                <li className={styles.listItem}>
                  Representative will follow up
                </li>
              </ul>
            </CcfBox>
          </CcfBox>
        </CcfBox>
      </Popover>
    </>
  );
}

export default CcfInteractionsCard;
